import React from "react";
import PropTypes from "prop-types";
import { X } from "lucide-react";
import { cn } from "../../lib/utils";

/**
 * MobileHeaderV3 - a generic mobile title bar: a heading on the left and an
 * optional close (✕) action on the right, over a white bar with a bottom
 * hairline. Mirrors the Figma "Modal / Title" block.
 *
 * Sibling to ShellHeaderV3 (used on list/dashboard pages). Reusable for any
 * mobile full-screen/modal surface (e.g. the "Start New Application" flow).
 *
 * Props:
 *  - title      : heading text (default "Start New Application")
 *  - onClose    : click handler for the ✕ (omit to hide it)
 *  - showClose  : force-show/hide the close action (defaults to !!onClose)
 *  - className  : extra classes on the <header>
 */
export function MobileHeaderV3({
  title = "Start New Application",
  onClose,
  showClose,
  className = ""
}) {
  const renderClose = showClose ?? Boolean(onClose);

  return (
    <header
      className={cn(
        "flex-shrink-0 flex items-center justify-between p-5 bg-neutral-1 border-b border-neutral-5",
        className
      )}
    >
      <div className="flex flex-1 items-center min-w-0">
        <p className="flex-1 min-w-0 break-words font-semibold text-xl leading-7 text-neutral-12">
          {title}
        </p>
      </div>

      {renderClose && (
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="shrink-0 flex items-center justify-center p-2 -m-2 rounded-md hover:bg-neutral-2 transition-colors"
        >
          <X className="w-7 h-7 text-neutral-12" />
        </button>
      )}
    </header>
  );
}

MobileHeaderV3.propTypes = {
  title: PropTypes.node,
  onClose: PropTypes.func,
  showClose: PropTypes.bool,
  className: PropTypes.string
};
